import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { GetQueue, GetAgent } from "../../Services/HeedifyServices";
import { parseQueueKpi, calculateTotalKpi, saveLayoutPositions, getLayoutPositions } from "../../Services/RealtimeDashboardService";
import { getAccessToken } from "../../Services/GraphServices";
import QueueCard from "./QueueCard";
import AgentsComponent from "./AgentsComponent";
import AgentStatusWidget from "./AgentStatusWidget";
import SlaWidget from "./SlaWidget";
import QueueListComponent from "./QueueListComponent";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./CSS/QueuesComponent.css";
import { Spinner } from "@fluentui/react-components";
const ResponsiveGridLayout = WidthProvider(Responsive);

const QueuesComponent = ({
  selectedQueues,
  agents,
  connection,
  isConnected,
  isEditMode,
  setIsEditMode,
  setSaveLayoutCallback,
  setResetLayoutCallback,
  setIsLayoutModified
}) => {
  const { t } = useTranslation();
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const currentTenantId = activeAccount?.tenantId;
  const userId = activeAccount?.localAccountId;
  const [token, setToken] = useState(null);
  const [queueKpis, setQueueKpis] = useState({});
  const [isLoadingKpis, setIsLoadingKpis] = useState(true);
  const [agentsData, setAgentsData] = useState([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  // Layout configuration for react-grid-layout
    const defaultLayout = {
    lg: [
      { i: 'maxWaitTime', x: 0, y: 0, w: 2, h: 2, minW: 1, minH: 2 },
      { i: 'todayCalls', x: 2, y: 0, w: 2, h: 2, minW: 1, minH: 2 },
      { i: 'overflow', x: 4, y: 0, w: 2, h: 2, minW: 1, minH: 2 },
      { i: 'liveCalls', x: 6, y: 0, w: 2, h: 2, minW: 1, minH: 2 },
      { i: 'abandonRate', x: 8, y: 0, w: 2, h: 2, minW: 1, minH: 2 },
      { i: 'avgWait', x: 10, y: 0, w: 2, h: 2, minW: 1, minH: 2 },
      { i: 'queueList', x: 0, y: 2, w: 6, h: 4, minW: 4, minH: 3 },
      { i: 'agentStatus', x: 6, y: 2, w: 6, h: 4, minW: 3, minH: 3 },
      { i: 'sla', x: 0, y: 6, w: 12, h: 4, minW: 3, minH: 3 },
      { i: 'agents', x: 0, y: 10, w: 12, h: 5, minW: 5, minH: 4 },
    ],
    md: [
      { i: 'maxWaitTime', x: 0, y: 0, w: 2, h: 2 },
      { i: 'todayCalls', x: 2, y: 0, w: 2, h: 2 },
      { i: 'overflow', x: 4, y: 0, w: 2, h: 2 },
      { i: 'liveCalls', x: 0, y: 2, w: 2, h: 2 },
      { i: 'abandonRate', x: 2, y: 2, w: 2, h: 2 },
      { i: 'avgWait', x: 4, y: 2, w: 2, h: 2 },
      { i: 'agentStatus', x: 0, y: 4, w: 3, h: 4 },
      { i: 'queueList', x: 0, y: 8, w: 6, h: 4 },
      { i: 'sla', x: 0, y: 12, w: 6, h: 4 },
      { i: 'agents', x: 0, y: 16, w: 6, h: 4 },
    ],
    sm: [
      { i: 'maxWaitTime', x: 0, y: 0, w: 2, h: 2 },
      { i: 'todayCalls', x: 2, y: 0, w: 2, h: 2 },
      { i: 'overflow', x: 0, y: 2, w: 2, h: 2 },
      { i: 'liveCalls', x: 2, y: 2, w: 2, h: 2 },
      { i: 'abandonRate', x: 0, y: 4, w: 2, h: 2 },
      { i: 'avgWait', x: 2, y: 4, w: 2, h: 2 },
      { i: 'agentStatus', x: 0, y: 6, w: 4, h: 4 },
      { i: 'queueList', x: 0, y: 10, w: 4, h: 4 },
      { i: 'sla', x: 0, y: 14, w: 4, h: 4 },
      { i: 'agents', x: 0, y: 18, w: 4, h: 4 },
    ],
  };

  const [layouts, setLayouts] = useState(defaultLayout);

  // Charger les positions sauvegardées depuis le service
  useEffect(() => {
    const loadLayoutPositions = async () => {
      if (!currentTenantId || !token) return;

      try {
        const response = await getLayoutPositions(currentTenantId, token);

        if (response.success && response.data) {
          const savedPositions = response.data;

          // Appliquer les positions sauvegardées au layout lg
          const updatedLgLayout = defaultLayout.lg.map(item => {
            const savedPos = savedPositions[item.i];
            if (savedPos) {
              return {
                ...item,
                x: savedPos.x,
                y: savedPos.y,
                w: savedPos.w,
                h: savedPos.h
              };
            }
            return item;
          });

          setLayouts({
            ...defaultLayout,
            lg: updatedLgLayout
          });
        }
      } catch (error) {
        console.error('Error loading layout positions:', error);
      }
    };

    loadLayoutPositions();
  }, [currentTenantId, token]);

  // Enregistrer le callback de sauvegarde
  useEffect(() => {
    if (setSaveLayoutCallback) {
      setSaveLayoutCallback(() => () => {
        // Collecter les positions actuelles du layout lg
        const currentPositions = {};

        if (layouts && layouts.lg) {
          layouts.lg.forEach(item => {
            currentPositions[item.i] = {
              id: item.i,
              x: item.x,
              y: item.y,
              w: item.w,
              h: item.h
            };
          });
        }

        saveDashboardPositions(currentPositions);
      });
    }
  }, [layouts, setSaveLayoutCallback]);

  // Enregistrer le callback de reset
  useEffect(() => {
    if (setResetLayoutCallback) {
      setResetLayoutCallback(() => () => {
        // Réinitialiser au layout par défaut
        setLayouts(defaultLayout);
        console.log('Layout reset to default');
      });
    }
  }, [setResetLayoutCallback]);

  const saveDashboardPositions = async (positions) => {
    try {
      if (!currentTenantId || !token) {
        console.log('Missing required data for saving layout');
        return;
      }

      // Sauvegarder via le nouveau service
      const response = await saveLayoutPositions(currentTenantId, positions, token);

      if (response.success) {
        console.log('Dashboard positions saved successfully:', response.message);
      } else {
        console.error('Error saving dashboard positions:', response.message);
      }
    } catch (error) {
      console.error('Error saving dashboard positions:', error);
    }
  };

  const handleLayoutChange = (layout, allLayouts) => {
    setLayouts(allLayouts);

    // Marquer le layout comme modifié en mode édition
    if (isEditMode && setIsLayoutModified) {
      setIsLayoutModified(true);
    }

    // Afficher le JSON du layout en console en mode édition
    if (isEditMode && allLayouts.lg) {
      const layoutPositions = {};
      allLayouts.lg.forEach(item => {
        layoutPositions[item.i] = {
          id: item.i,
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h
        };
      });
      console.log('Layout Positions JSON:', JSON.stringify(layoutPositions, null, 2));
    }
  };

  // selectedQueues contient déjà les objets complets des queues
  const queuesArray = useMemo(() => {
    if (!selectedQueues || selectedQueues.length === 0) return [];
    return selectedQueues;
  }, [selectedQueues]);

  // Extraire les scopes uniques des queues sélectionnées
  const scopesFromQueues = useMemo(() => {
    const scopes = new Set();
    queuesArray.forEach(queue => {
      if (queue.scope) scopes.add(queue.scope);
    });
    return Array.from(scopes);
  }, [queuesArray]);

  // Utiliser le premier scope disponible pour tenantId
  const tenantId = scopesFromQueues.length > 0 ? scopesFromQueues[0] : currentTenantId;
  const scopeName = scopesFromQueues.length > 0 ? scopesFromQueues[0] : 'default';

  // Étape 1: Acquérir le token via getAccessToken
  useEffect(() => {
    const fetchToken = async () => {
      if (activeAccount && instance) {
        try {
          const accessToken = await getAccessToken(instance, activeAccount);
          setToken(accessToken);
        } catch (error) {
          console.error("Error acquiring token:", error);
        }
      }
    };

    fetchToken();
  }, [instance, activeAccount]);



  // Fusionner et calculer le KPI total de toutes les queues sélectionnées
  const totalKpi = useMemo(() => {
    // calculateTotalKpi attend un objet {queueId: kpi}, pas un array
    return calculateTotalKpi(queueKpis);
  }, [queueKpis]);

  // Combiner tous les agentDetails de toutes les queues
  const allAgentDetails = useMemo(() => {
    const combinedAgentDetails = {};

    Object.values(queueKpis).forEach(kpi => {
      if (kpi.agentDetails) {
        Object.entries(kpi.agentDetails).forEach(([agentId, agentData]) => {
          // Si l'agent existe déjà, on garde la dernière mise à jour
          if (!combinedAgentDetails[agentId] ||
            (agentData.lastUpdated && agentData.lastUpdated > (combinedAgentDetails[agentId].lastUpdated || 0))) {
            combinedAgentDetails[agentId] = agentData;
          }
        });
      }
    });

    return combinedAgentDetails;
  }, [queueKpis]);

  // Combiner tous les reasonCodes de toutes les queues
  const allReasonCodes = useMemo(() => {
    const combinedReasonCodes = [];

    Object.values(queueKpis).forEach(kpi => {
      if (kpi.reasonCodes && Array.isArray(kpi.reasonCodes)) {
        combinedReasonCodes.push(...kpi.reasonCodes);
      }
    });

    return combinedReasonCodes;
  }, [queueKpis]);

  // Concaténer tous les agentIds uniques des queues sélectionnées
  // Garder en mémoire tous les agents déjà vus pour éviter qu'ils disparaissent
  const allAgentIds = useMemo(() => {
    const agentIdsSet = new Set();

    Object.values(queueKpis).forEach(kpi => {
      if (kpi.agentDetails) {
        Object.keys(kpi.agentDetails).forEach(agentId => {
          agentIdsSet.add(agentId);
        });
      }
    });

    return Array.from(agentIdsSet);
  }, [queueKpis]);

  // Garder en mémoire tous les agents déjà chargés pour éviter qu'ils disparaissent
  const [allSeenAgentIds, setAllSeenAgentIds] = useState(new Set());
  
  useEffect(() => {
    if (allAgentIds.length > 0) {
      setAllSeenAgentIds(prev => {
        const updated = new Set(prev);
        allAgentIds.forEach(id => updated.add(id));
        return updated;
      });
    }
  }, [allAgentIds]);

  // Utiliser les IDs vus pour charger les agents
  const agentIdsToLoad = useMemo(() => {
    const ids = Array.from(allSeenAgentIds);
    console.log(`📋 [Agents] Total unique agent IDs to load: ${ids.length}`, ids);
    return ids;
  }, [allSeenAgentIds]);

  // Étape 2: Charger les données en temps réel pour chaque queue individuellement
  useEffect(() => {
    // Attendre que le token soit disponible
    if (!token) {
      return;
    }

    if (!currentTenantId || !userId || !queuesArray || queuesArray.length === 0) {
      console.log("Missing required data:", { currentTenantId, userId, queuesArrayLength: queuesArray?.length });
      // Réinitialiser queueKpis si aucune queue n'est sélectionnée
      setQueueKpis({});
      setIsLoadingKpis(false);
      return;
    }


    const loadQueuesData = async () => {
      setIsLoadingKpis(true);
      // Réinitialiser queueKpis pour ne garder que les queues sélectionnées
      setQueueKpis({});

      try {
        // Créer un objet temporaire pour stocker tous les KPI
        const newQueueKpis = {};

        // Mapper chaque queue et appeler GetQueue individuellement
        const queuePromises = queuesArray.map(async (queue) => {
          const queueId = queue.id || queue.RowKey;
          const scope = queue.scope;

          try {
            // Appeler GetQueue pour récupérer les données de cette queue
            const queueData = await GetQueue(currentTenantId, userId, queueId, scope, token);
console.log(`Data for queue ${queueId}:`, queueData);
            if (queueData) {
              // Parser les KPI de cette queue
              const parsedKpi = parseQueueKpi(queueData);
              newQueueKpis[queueId] = parsedKpi;
            }
          } catch (error) {
            console.error(`Error loading queue ${queueId}:`, error);
          }
        });

        // Attendre que toutes les queues soient chargées
        await Promise.all(queuePromises);

        // Mettre à jour le state une seule fois avec toutes les queues
        setQueueKpis(newQueueKpis);
        setIsLoadingKpis(false);
      } catch (err) {
        console.error("Error loading queues data:", err);
        setIsLoadingKpis(false);
      }
    };

    loadQueuesData();
  }, [token, currentTenantId, userId, queuesArray]);

  // S'abonner aux mises à jour SignalR pour toutes les queues
  useEffect(() => {
    if (!connection || !isConnected || !queuesArray || queuesArray.length === 0) return;

    const subscriptions = [];
    const registeredEvents = new Set(); // Éviter les doublons d'enregistrement

    queuesArray.forEach(queue => {
      const queueId = queue.id || queue.RowKey;
      // Le serveur envoie l'événement avec le nom exact de la queue (sans préfixe)
      const eventName = queueId;

      // Éviter d'enregistrer le même événement plusieurs fois
      if (registeredEvents.has(eventName)) {
        console.warn(`Event ${eventName} already registered, skipping duplicate`);
        return;
      }

      const handler = (updatedKpi) => {
        console.log(`📊 [SignalR] Queue ${queueId} - Event received:`, updatedKpi);
       
        // Remplacer complètement le KPI avec les nouvelles données du serveur
        setQueueKpis(prev => ({
          ...prev,
          [queueId]: parseQueueKpi(updatedKpi)
        }));
      };

      connection.on(eventName, handler);
      subscriptions.push({ eventName, handler });
      registeredEvents.add(eventName);
      console.log(`✅ [SignalR] Subscribed to queue event: ${eventName}`);
    });

    return () => {
      subscriptions.forEach(({ eventName, handler }) => {
        connection.off(eventName, handler);
        console.log(`❌ [SignalR] Unsubscribed from queue event: ${eventName}`);
      });
    };
  }, [connection, isConnected, queuesArray]);

  // Étape 3: Charger les détails des agents
  useEffect(() => {
    if (!token || !currentTenantId || !userId || agentIdsToLoad.length === 0) {
      setAgentsData([]);
      return;
    }

    const loadAgentsData = async () => {
      setIsLoadingAgents(true);
      try {
        const agentPromises = agentIdsToLoad.map(async (agentId) => {
          try {
            const agentData = await GetAgent(currentTenantId, userId, agentId, token);
            if (agentData) {
              // Enrichir avec les infos de queueKpis
              const agentQueues = [];
              Object.values(queueKpis).forEach(kpi => {
                if (kpi.agentDetails && kpi.agentDetails[agentId]) {
                  agentQueues.push({
                    queueId: kpi.queueId,
                    queueName: kpi.queueName,
                    state: kpi.agentDetails[agentId].state || kpi.agentDetails[agentId].status
                  });
                }
              });

              return {
                ...agentData,
                id: agentId,
                queues: agentQueues
              };
            }
          } catch (error) {
            console.error(`Error loading agent ${agentId}:`, error);
          }
          return null;
        });

        const agents = await Promise.all(agentPromises);
        setAgentsData(agents.filter(agent => agent !== null));
        setIsLoadingAgents(false);
      } catch (error) {
        console.error("Error loading agents data:", error);
        setIsLoadingAgents(false);
      }
    };

    loadAgentsData();
  }, [token, currentTenantId, userId, agentIdsToLoad, queueKpis]);

  // Fonction pour formater les pourcentages
  const formatPercentage = (value) => {
    if (value == null || value === "--" || value === 0) return "0%";
    const numberValue = parseFloat(value);
    if (Number.isInteger(numberValue)) {
      return `${numberValue}%`;
    }
    const formattedValue = numberValue.toFixed(2).replace(/\.?0+$/, "");
    return `${formattedValue}%`;
  };

  return (
    <div className="queues-component">
      {/* KPI Cards Grid */}
      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        breakpoints={{ lg: 1200, md: 996, sm: 996, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 6, sm: 4, xs: 2, xxs: 1 }}
        rowHeight={60}
        onLayoutChange={handleLayoutChange}
        isDraggable={isEditMode}
        isResizable={isEditMode}
      >
        {/* Max Wait Time Widget */}
        <div key="maxWaitTime" className="kpi-card">
          <div className="kpi-card-header drag-handle">
            <button className="kpi-card-menu">⋮⋮</button>
            <h3>{t("REALTIME.MAX_WAIT_TIME")}</h3>

          </div>
          <div className="kpi-card-value">
            {isLoadingKpis ? (
              <Spinner size="large" />
            ) : (
              totalKpi?.kpi?.maxWaitTime || "00:00:00"
            )}
          </div>
        </div>

        {/* Today Calls Card */}
        <div key="todayCalls" className="kpi-card">
          <div className="kpi-card-header drag-handle">
            <button className="kpi-card-menu">⋮⋮</button>
            <h3>{t("REALTIME.CALLS_IN_QUEUE")}</h3>
          </div>
          <div className="kpi-card-value">
            {isLoadingKpis ? (
              <Spinner size="small" />
            ) : (
              totalKpi?.kpi?.todayCalls || 0
            )}
          </div>
          {!isLoadingKpis && <div className="kpi-card-extra today">{t("REALTIME.TODAY")}</div>}
        </div>

        {/* Overflow Widget */}
        <div key="overflow" className="kpi-card">
          <div className="kpi-card-header drag-handle">
            <button className="kpi-card-menu">⋮⋮</button>
            <h3>{t("REALTIME.OVERFLOW")}</h3>
          </div>
          <div className="kpi-card-value">
            {isLoadingKpis ? (
              <Spinner size="small" />
            ) : (
              formatPercentage(totalKpi?.kpi?.overflowRate)
            )}
          </div>
        </div>

        {/* Live Calls Card */}
        <div key="liveCalls" className="kpi-card">
          <div className="kpi-card-header drag-handle">
            <button className="kpi-card-menu">⋮⋮</button>
            <h3>{t("REALTIME.LIVE_CALLS")}</h3>
          </div>
          <div className="kpi-card-value">
            {isLoadingKpis ? (
              <Spinner size="small" />
            ) : (
              totalKpi?.kpi?.callsInQueue || 0
            )}
          </div>
          {!isLoadingKpis && <div className="kpi-card-extra live">{t("REALTIME.LIVE")}</div>}
        </div>

        {/* Abandon Rate Card */}
        <div key="abandonRate" className="kpi-card">
          <div className="kpi-card-header drag-handle">
            <button className="kpi-card-menu">⋮⋮</button>
            <h3>{t("REALTIME.ABANDON_RATE")}</h3>
          </div>
          <div className="kpi-card-value">
            {isLoadingKpis ? (
              <Spinner size="small" />
            ) : (
              formatPercentage(totalKpi?.kpi?.abandonRate)
            )}
          </div>
        </div>

        {/* Average Wait Time Widget */}
        <div key="avgWait" className="kpi-card">
          <div className="kpi-card-header drag-handle">
            <button className="kpi-card-menu">⋮⋮</button>
            <h3>{t("REALTIME.AVG_WAIT_TIME")}</h3>
          </div>
          <div className="kpi-card-value">
            {isLoadingKpis ? (
              <Spinner size="small" />
            ) : (
              totalKpi?.kpi?.avgWaitTime || "00:00:00"
            )}
          </div>
        </div>

        {/* Availability Widget */}
        {totalKpi?.agents && (
          <div key="agentStatus" className="grid-widget">
            {isLoadingKpis ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Spinner size="medium" />
              </div>
            ) : (
              <AgentStatusWidget
                agentStats={totalKpi.agents}
                agentDetails={allAgentDetails}
                reasonCodes={allReasonCodes}
              />
            )}
          </div>
        )}

        {/* SLA Widget */}
        <div key="sla" className="grid-widget">
          {isLoadingKpis ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Spinner size="medium" />
            </div>
          ) : (
            <SlaWidget
              totalSla={totalKpi?.kpi?.sla}
              queueKpis={queueKpis}
            />
          )}
        </div>

        {/* Queue List */}
        <div key="queueList" className="grid-widget">
          {isLoadingKpis ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Spinner size="medium" />
            </div>
          ) : (
            <QueueListComponent
              queues={queuesArray}
              queueKpis={queueKpis}
            />
          )}
        </div>

        {/* Agents Widget */}
        <div key="agents" className="grid-widget agents-widget">
          <h3>{t("REALTIME.AGENTS")} ({agentsData.length}) <span className="drag-handle">⋮⋮</span></h3>
          <AgentsComponent
            tenantId={tenantId}
            agents={agentsData}
            isLoading={isLoadingAgents}
            connection={connection}
            isConnected={isConnected}
          />
        </div>
      </ResponsiveGridLayout>
    </div>
  );
};

export default QueuesComponent;
